import React from 'react';
import gql from 'graphql-tag';
import { withApollo } from '@apollo/react-hoc';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { CSVLink } from 'react-csv';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { escapeCsvCommands } from '../../utils';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Message from '../../components/Message';
import Header from '../../components/Header';

import './styles.css';

const query = gql`
  query repVisitsContacts {
    viewer {
      id
      repVisitsContacts (first: 5000) {
        edges {
          node {
            id
            highSchool {
              id
              name
              address {
                field_hs_elna_address_line1
                field_hs_elna_state_latest
                field_hs_elna_city
                field_hs_eln_zip
              }
              naviance {
                field_hs_elna_phone
              }
              repVisitsAccount {
                  id
                  primaryContact {
                    id
                    mail
                    field_first_name
                    field_last_name
                    field_full_name
                    field_job_position
                    field_profile_picture_file_url
                  }
              }
            }
            notes
          }
        }
      }
    }
  }
`;

export const messages = defineMessages({
  exportContacts: {
    id: 'repvisits.contacts.exportContacts',
    defaultMessage: 'Export Contacts',
  },
  exportContactsHeader: {
    id: 'repvisits.contacts.exportContactsHeader',
    defaultMessage: 'Download RepVisits Contacts?',
  },
  exportContactsContent: {
    id: 'repvisits.contacts.exportContactsContent',
    defaultMessage: 'Loading contacts for download',
  },
  exportContactsCancel: {
    id: 'repvisits.contacts.exportContactsCancel',
    defaultMessage: 'Cancel',
  },
  exportContactsDownload: {
    id: 'repvisits.contacts.exportContactsDownload',
    defaultMessage: 'Download',
  },
});

class ExportContacts extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
    loading: PropTypes.bool,
    openModal: PropTypes.bool,
    client: PropTypes.shape({
      query: PropTypes.func.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      openModal: false,
      error: false,
      viewer: null,
    };
  }

  closeModal = () => {
    this.setState({ openModal: false });
  };

  exportRVContacts = async (e) => {
    e.preventDefault();
    this.setState({ openModal: true, loading: true });
    const state = { loading: false };
    try {
      const data = await this.props.client.query({
        query,
      });
      state.viewer = data.data.viewer;
    } catch (err) {
      state.error = true;
    }
    this.setState(state);
  };

  render() {
    const { openModal, loading, viewer, error } = this.state;
    const contacts = get(viewer, 'repVisitsContacts.edges', []).map((contact) => contact.node);

    const csvContactData = contacts.map((contact) => ({
      'First Name': escapeCsvCommands(get(contact, 'highSchool.repVisitsAccount.primaryContact.field_first_name')) || '',
      'Last Name': escapeCsvCommands(get(contact, 'highSchool.repVisitsAccount.primaryContact.field_last_name')) || '',
      'Institution': escapeCsvCommands(get(contact, 'highSchool.name')) || '',
      'Institution Address Line 1': escapeCsvCommands(get(contact, 'highSchool.address.field_hs_elna_address_line1')) || '',
      'Institution City': escapeCsvCommands(get(contact, 'highSchool.address.field_hs_elna_city')) || '',
      'Institution State': escapeCsvCommands(get(contact, 'highSchool.address.field_hs_elna_state_latest')) || '',
      'Institution Postal Code': escapeCsvCommands(get(contact, 'highSchool.address.field_hs_eln_zip')) || '',
      'Office Phone': escapeCsvCommands(get(contact, 'highSchool.naviance.field_hs_elna_phone')) || '',
      'Email': escapeCsvCommands(get(contact, 'highSchool.repVisitsAccount.primaryContact.mail')) || '',
      'Notes': escapeCsvCommands(get(contact, 'notes')) || '',
    }));

    return (
      <Modal
        dimmer="inverted"
        styleName="modal-export-contact"
        open={openModal}
        trigger={(
          <Button
            primary
            floated="right"
            size="small"
            styleName="export-contacts"
            onClick={this.exportRVContacts}
            data-cy="repvisit-export-contacts-btn"
          >
            <FormattedMessage {...messages.exportContacts} />
          </Button>
)}
      >
        <Header>
          <FormattedMessage {...messages.exportContactsHeader} />
          {error && <Message error header="Oops, something went wrong" />}
        </Header>
        <Modal.Actions>
          <Button onClick={this.closeModal} data-cy="repvisit-export-contacts-cancel-btn">
            <FormattedMessage {...messages.exportContactsCancel} />
          </Button>
          <Button
            as={CSVLink}
            primary
            onClick={this.closeModal}
            data={csvContactData}
            filename="repVisits-contacts.csv"
            target="_blank"
            loading={loading}
            disabled={loading || error}
            data-cy="repvisit-export-contacts-download-btn"
          >
            <FormattedMessage {...messages.exportContactsDownload} />
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

export const ExportContactsComponent = injectIntl(ExportContacts);
export default withApollo(ExportContactsComponent);
